<template>
  <Modal v-model="openedState" class="modal-product">
    <template #content>
      <div class="position-relative border-bottom">
        <div class="d-flex align-items-center gap-3 m-20p">
          <div class="avatar" :style="{ backgroundColor: selectedProduct?.image_background || '#F3F3F3' }">
            <img
              :src="selectedProduct?.image?.data?.proxy.url_preview"
              :alt="selectedProduct?.name"
              v-if="selectedProduct?.image?.data?.proxy.url_preview" />
          </div>
          <div class="d-flex flex-column flex-grow-1">
            <h4 class="mb-1 me-3 product-title">{{ selectedProduct?.name }}</h4>
            <p class="text-muted mb-0 product-subtitle" v-html="productLabel"></p>
          </div>
          <button type="button" class="btn-close" @click="openedState = false" />
        </div>
      </div>
      <div class="modal-sections" :key="refreshFormKey">
        <div class="modal-section" v-if="productOptions.length">
          <div class="d-flex flex-column gap-3 m-20p">
            <CollapseContent
              :modelValue="isExpanded('productOptions', option.id)"
              @update:modelValue="() => toggleSection('productOptions', option.id)"
              :name="option.title"
              v-for="option in productOptions"
              :key="option.id">
              <template #description v-if="!isExpanded('productOptions')">
                <div v-if="hasSelectedOptions" class="text-muted mb-0">
                  <p class="mb-1 d-flex align-items-center gap-2">
                    <IconCircleCheck class="text-blue flex-shrink-0"></IconCircleCheck>
                    <span class="fw-semibold">{{ selectedProductOptionsDisplay }}</span>
                  </p>
                </div>
                <p class="text-muted mb-0" v-else>
                  <span class="text-muted">{{ $t('products.modal.options.no_selected') }}</span>
                </p>
              </template>
              <p class="text-muted">
                <small>{{ option.description }}</small>
              </p>

              <div class="row g-3">
                <div class="col-6" v-for="opt in option.field_config.cards" :key="opt.title">
                  <div
                    class="card card-sm option-card border-3"
                    @click="productToSave.selectedOptions[option.id] = opt.value"
                    :class="{ 'border-primary': productToSave.selectedOptions[option.id] === opt.value }">
                    <div class="card-body d-flex flex-column justify-content-between">
                      <IconCircleCheckFilled
                        class="selected-icon text-primary"
                        v-if="productToSave.selectedOptions[option.id] === opt.value" />
                      <h5>{{ opt.title }}</h5>
                      <p>{{ opt.description }}</p>
                      <h5 class="mt-auto">{{ formatCurrency(opt.cost, opt.cost_currency || productCurrency) }}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </CollapseContent>
          </div>
        </div>
        <div class="modal-section" v-else-if="selectedProduct?.type === 'single'">
          <div class="m-20p">
            <h4>Price</h4>
            <p class="text-muted mb-0">
              <span class="fw-semibold">{{
                formatCurrency(selectedProduct.price!, selectedProduct.price_currency)
              }}</span>
            </p>
          </div>
        </div>
        <div class="modal-section" v-if="showBudgetFields">
          <div class="m-20p">
            <CollapseContent
              :modelValue="isExpanded('budget')"
              @update:modelValue="() => toggleSection('budget')"
              :name="$t('products.modal.budget.title')"
              :description="`${$t('total_budget')} ${productToSave.budget} ${defaultCurrency}`"
              :error="budgetInvalid">
              <template #description v-if="!isExpanded('budget')">
                <p class="text-muted mb-0">
                  {{ $t('total_budget') }} {{ $t('is') }}
                  <span class="fw-semibold">{{ formatCurrency(productToSave.budget!, productCurrency) }}</span>
                </p>
              </template>
              <BudgetPicker
                class="mt-2"
                v-model="productToSave.budget!"
                :limitations="limitations"
                :startdate="productToSave.startdate"
                :enddate="productToSave.enddate"
                @invalid-budget="(state) => (budgetInvalid = state)"
                :currency="productCurrency" />
            </CollapseContent>
          </div>
        </div>
        <div class="modal-section" v-if="showDateFields">
          <div class="m-20p">
            <CollapseContent
              :modelValue="isExpanded('dates')"
              @update:modelValue="() => toggleSection('dates')"
              :name="$t('products.modal.duration.title')"
              :error="datesInvalid">
              <template #description v-if="!isExpanded('dates')">
                <p class="text-muted mb-0" v-if="productToSave.startdate && productToSave.enddate">
                  <span class="fw-semibold">{{ formatDate(productToSave.startdate) }}</span>
                  -
                  <span class="fw-semibold">{{ formatDate(productToSave.enddate) }}</span>
                </p>
                <p class="text-muted mb-0" v-else>
                  <span class="text-muted">{{ $t('datepicker.select_start') }}</span>
                </p>
              </template>
              <FormDatePick
                class="mt-2"
                v-model:startdate="productToSave.startdate"
                v-model:enddate="productToSave.enddate"
                :defaultDuration="defaultDuration"
                :limitations="limitations"
                :hide-duration="hideDateDuration"
                :allow-infinite="allowInfiniteDateDuration"
                @invalid-dates="(state) => (datesInvalid = state)" />
            </CollapseContent>
          </div>
        </div>
        <div class="modal-section" v-if="showMetricSlider">
          <div class="m-20p">
            <CollapseContent
              :modelValue="isExpanded('metricSlider')"
              @update:modelValue="() => toggleSection('metricSlider')"
              :name="$t('products.modal.metric.title')">
              <template #description v-if="!isExpanded('metricSlider')">
                <p class="text-muted mb-0">
                  {{ $t('selected') }}:&nbsp;
                  <span class="fw-semibold">{{ productToSave.metric_goal }} &nbsp;</span>
                  <span class="fw-semibold">{{ selectedProduct!.metric }}</span>
                </p>
              </template>
              <RangePicker
                class="mt-3"
                :label="$t('products.modal.metric.goal.title')"
                v-model.number="productToSave.metric_goal"
                :max-range="limitations.metric_goal_max"
                :min-range="limitations.metric_goal_min" />
            </CollapseContent>
          </div>
        </div>
        <div class="modal-section" v-if="showSpendingSlider">
          <div class="m-20p">
            <CollapseContent
              :modelValue="isExpanded('spendingSlider')"
              @update:modelValue="() => toggleSection('spendingSlider')"
              :name="$t('products.modal.spending.title')">
              <template #description v-if="!isExpanded('spendingSlider')">
                <p class="text-muted mb-0">
                  {{ $t('selected') }}:&nbsp;
                  <span class="fw-semibold"
                    >{{ formatCurrency(productToSave.spending_limit || 0, productCurrency) }} &nbsp;</span
                  >
                </p>
              </template>
              <RangePicker
                class="mt-3"
                :label="$t('products.modal.spending.slider.title')"
                v-model.number="productToSave.spending_limit"
                :max-range="limitations.spending_limit_max"
                :min-range="limitations.spending_limit_min"
                :isPrice="true"
                :currency="productCurrency" />
            </CollapseContent>
          </div>
        </div>
        <div class="modal-section" v-if="showSubscription">
          <div class="m-20p">
            <h4 class="py-2">{{ $t('products.modal.subscription.confirmation') }}</h4>
          </div>
        </div>
        <div class="modal-section" v-if="showSplits">
          <div class="m-20p">
            <CollapseContent
              :modelValue="isExpanded('splits')"
              @update:modelValue="() => toggleSection('splits')"
              :name="$t('products.modal.splits.title')">
              <p class="text-muted mb-3">
                <small>{{ $t('products.modal.splits.description') }}</small>
              </p>
              <template #description v-if="!isExpanded('splits')">
                <div v-if="suggestingVBudget" class="mt-2 d-flex align-items-center gap-2">
                  <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
                  <small class="text-muted">{{ $t('products.modal.splits.suggesting') }}</small>
                </div>
                <SplitAllocationItem
                  v-else
                  :leftLabel="productToSave.vBudget?.name"
                  class="mt-2 cursor-pointer"
                  @click="toggleSection('splits')">
                  <template #rightLabel>
                    <small class="m-0 text-muted">
                      {{ formatCurrency(remainingSplitBudget) }}
                      <span class="fw-normal"> {{ $t('of') }} </span>
                      {{ formatCurrency(productToSave.vBudget?.total_budget || 0) }}
                    </small>
                  </template>
                </SplitAllocationItem>
                <p class="text-danger m-0 mt-1 small" v-if="splitBudgetBelowMinimum">
                  {{ $t('products.modal.splits.below_minimum_error') }}
                </p>
              </template>
              <div v-if="suggestingVBudget" class="d-flex justify-content-center py-3">
                <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
              </div>
              <template v-else>
                <SplitSelector
                  v-model="productToSave.vBudget"
                  :tree="vBudgetTree"
                  :expectedBudget="productPrice || null" />
                <p class="text-danger m-0 small" v-if="splitBudgetBelowMinimum">
                  {{ $t('products.modal.splits.below_minimum_error') }}
                </p>
              </template>
            </CollapseContent>
          </div>
        </div>
      </div>
      <div class="modal-footer py-3 border-top">
        <h2>{{ footerInfo }}</h2>
        <button
          class="btn btn-primary"
          data-testid="oc-product-modal-continue-button"
          :class="{ disabled: !readyToSave }"
          @click="saveProduct">
          {{ $t('products.modal.button.continue') }}
          <IconArrowRight :size="16" class="ms-1" />
        </button>
      </div>
    </template>
  </Modal>
</template>
// TODO: currency is fixed currently
<script setup lang="ts">
import BudgetPicker from '@/components/common/BudgetPicker.vue';
import CollapseContent from '@/components/common/CollapseContent.vue';
import FormDatePick from '@/components/common/FormDatePick.vue';
import { Modal } from '@prospective/lithium';
import { computed, nextTick, ref, watch } from 'vue';
import { defaultCampaignDuration, defaultCurrency } from '@/config/app';
import { IconArrowRight, IconCircleCheck, IconCircleCheckFilled } from '@tabler/icons-vue';
import SplitAllocationItem from '@/components/common/SplitAllocationItem.vue';
import SplitSelector from '@/components/common/SplitSelector.vue';
import { formatCurrency } from '@/locale/format';
import { formatDate, formatDateTime, PJAS_DATE_FORMAT } from '@/filters/dateFormat';
import type { IProduct, IProductLimitations, IProductOption, IProductToSave, IProductVBudget } from '@/types/product';
import { addDays, subSeconds, startOfTomorrow } from 'date-fns';
import RangePicker from '@/components/common/RangePicker.vue';
import { calculateProductModalPrice, extractSelectedProductOptions } from './functions';
import { fetchActiveBudget, fetchBudgetTree, suggestVBudget } from '@/api/budget';
import { useToast } from 'vue-toastification';
import type { IActiveBudget, IBudgetTreeNode } from '@/types/budget';
import translator from '@/locale/translator';
import { useOrderCreationStore } from '@/stores/orderCreation';
import { useActiveBudgetStore } from '@/stores/activeBudget';
import { storeToRefs } from 'pinia';

const { order } = storeToRefs(useOrderCreationStore());
const activeBudgetStore = useActiveBudgetStore();

const defaultLimits: IProductLimitations = {
  budget_max: Infinity,
  budget_daily_max: Infinity,
  duration_max: Infinity,
  budget_min: 0,
  budget_daily_min: 0,
  duration_min: 0,
  metric_goal_max: 0,
  metric_goal_min: Infinity,
  spending_limit_min: 0,
  spending_limit_max: Infinity,
};

const defaultVBudgetTree: any = {
  type: 'customer_budget',
  availability: {} as any,
  children: [],
  customer_budget: { data: {} as IActiveBudget },
};
const defaultSelectedVbudget = ref<IProductVBudget>({
  id: 0,
  name: '',
  currency: defaultCurrency,
  total_budget: 0,
  remaining_budget: 0,
  children: [],
});

const selectedProduct = defineModel<IProduct | undefined>();

const emit = defineEmits<{
  (e: 'saveProduct', product: IProduct, paramsToSave: Partial<IProductToSave>): void;
}>();

// section accordion state
const expandedSection = ref<string | null>(null);
const isExpanded = (sectionName: string, subSection = 0) => expandedSection.value === `${sectionName}-${subSection}`;
const toggleSection = (sectionName: string, subSection = 0) => {
  expandedSection.value =
    expandedSection.value === `${sectionName}-${subSection}` ? null : `${sectionName}-${subSection}`;
};

const limitations = ref<IProductLimitations>({ ...defaultLimits });

const startdate = startOfTomorrow();
const enddate = subSeconds(addDays(startdate, defaultCampaignDuration), 1);
const productToSave = ref<IProductToSave>({
  id: 0,
  startdate: formatDateTime(startdate, PJAS_DATE_FORMAT),
  enddate: formatDateTime(enddate, PJAS_DATE_FORMAT),
  budget: 1000,
  selectedOptions: {},
  metric_goal: undefined,
  spending_limit: undefined,
});

const vBudgetTree = ref<IBudgetTreeNode>(defaultVBudgetTree);
const activeBudgetId = ref<number | null>(null);
const suggestingVBudget = ref(false);
const budgetInvalid = ref(false);
const datesInvalid = ref(false);
const refreshFormKey = ref(0);

/* ------------------------------- ON CREATED ------------------------------- */
fetchActiveBudget()
  .then(async (activeBudget) => {
    const budget = activeBudget?.data;
    if (budget) {
      activeBudgetId.value = budget.id;
      await fetchSplits(budget.id).then(() => {
        // set default selected split to the active budget
        defaultSelectedVbudget.value = {
          id: budget.id,
          name: budget.name,
          type: 'customer_budget',
          currency: budget.currency,
          total_budget: vBudgetTree.value.availability?.available_now || 0,
          remaining_budget: vBudgetTree.value.availability?.available_unallocated || 0,
          children: [],
        };
        productToSave.value.vBudget = JSON.parse(JSON.stringify(defaultSelectedVbudget.value));
      });
      await applySuggestedVBudget(budget.id);
    }
  })
  .catch((error) => {
    console.error('Error fetching active budget:', error);
  });

/* -------------------------------- COMPUTED -------------------------------- */
const showBudgetFields = computed(() => ['variable'].includes(selectedProduct.value?.type || ''));
const showDateFields = computed(() =>
  ['variable', 'contingent', 'flatrate', 'single'].includes(selectedProduct.value?.type || '')
);
const showSplits = computed(
  () => (selectedProduct.value?.can_use_budget_splits && vBudgetTree.value.children.length > 0) || false
);
const showMetricSlider = computed(() => ['target'].includes(selectedProduct.value?.type || ''));
const showSpendingSlider = computed(() => ['target'].includes(selectedProduct.value?.type || ''));
const hideDateDuration = computed(() => ['contingent', 'single'].includes(selectedProduct.value?.type || ''));
const allowInfiniteDateDuration = computed(() =>
  ['subscription', 'flatrate'].includes(selectedProduct.value?.type || '')
);
const showSubscription = computed(() => ['subscription'].includes(selectedProduct.value?.type || ''));

const productOptions = computed<IProductOption[]>(() => {
  return selectedProduct.value?.product_options?.data || [];
});

const openedState = computed<boolean>({
  get: () => !!selectedProduct.value,
  set: (value) => {
    if (!value) selectedProduct.value = undefined;
  },
});

const defaultDuration = computed(() => {
  return Number(
    selectedProduct.value?.duration ||
      (limitations.value.duration_min !== 0 ? limitations.value.duration_min : defaultCampaignDuration)
  );
});

const productLabel = computed(() => {
  const label = selectedProduct.value?.allocation_label.replace(
    /\*\*\*(.*?)\*\*\*/g,
    '<span class="text-muted">$1</span>'
  );
  if (label && selectedProduct.value?.type === 'target') {
    return `<span class="fw-bold">${translator.get('products.default')}</span>: ${label}`;
  }
  return label || '';
});

const readyToSave = computed<boolean>(() => {
  if (!selectedProduct.value || splitBudgetBelowMinimum.value) return false;

  try {
    if (showDateFields.value && datesInvalid.value) return false;
    if (showBudgetFields.value && budgetInvalid.value) return false;
    if (showMetricSlider.value) {
      if (!productToSave.value.metric_goal) return false;
      const metricValid =
        productToSave.value.metric_goal >= limitations.value.metric_goal_min! &&
        productToSave.value.metric_goal <= limitations.value.metric_goal_max!;
      if (!metricValid) return false;
    }
    if (showSpendingSlider.value) {
      if (!productToSave.value.spending_limit) return false;
      const spendingValid =
        productToSave.value.spending_limit >= limitations.value.spending_limit_min! &&
        productToSave.value.spending_limit <= limitations.value.spending_limit_max!;
      if (!spendingValid) return false;
    }

    if (productOptions.value.length) {
      const chosenOptions = Object.values(productToSave.value.selectedOptions);
      if (!chosenOptions.length) return false;
      return chosenOptions.every((opt) => !!opt);
    }

    return true;
  } catch (error) {
    console.error('Error validating product:', error);
    return false;
  }
});

const productPrice = computed(() => calculateProductModalPrice(selectedProduct.value, productToSave.value));

const productCurrency = computed(() => {
  if (!selectedProduct.value) return defaultCurrency;

  if (selectedProduct.value.type === 'single') {
    return selectedProduct.value.price_currency;
  } else if (selectedProduct.value.type === 'variable') {
    return selectedProduct.value.budget_currency;
  } else if (selectedProduct.value.type === 'target') {
    return selectedProduct.value.spending_limit_currency;
  }
  return defaultCurrency;
});

const footerInfo = computed(() => {
  if (productPrice.value !== undefined) return formatCurrency(productPrice.value, productCurrency.value);

  if (showMetricSlider.value) return `${productToSave.value.metric_goal || 0} ${selectedProduct.value?.metric}`;

  return '';
});

const availableSections = computed(() => {
  const sections: string[] = [];
  if (productOptions.value.length) sections.push('productOptions');
  if (showBudgetFields.value) sections.push('budget');
  if (showDateFields.value) sections.push('dates');
  if (showMetricSlider.value) sections.push('metricSlider');
  if (showSpendingSlider.value) sections.push('spendingSlider');
  if (showSubscription.value) sections.push('subscription');
  if (showSplits.value) sections.push('splits');
  return sections;
});

const selectedProductOptionsDisplay = computed(() => {
  if (!productOptions.value.length) return null;

  const selectedOptions = extractSelectedProductOptions(productToSave.value.selectedOptions, productOptions.value);
  const optionTexts = Object.values(selectedOptions).map((opt) => opt.title);

  if (optionTexts.length === 0) return null;

  if (optionTexts.length > 2) {
    return `${optionTexts.slice(0, 2).join(', ')} +${optionTexts.length - 2} more`;
  }

  return optionTexts.join(', ');
});

const hasSelectedOptions = computed(() => {
  return (
    Object.keys(productToSave.value.selectedOptions).length > 0 &&
    Object.values(productToSave.value.selectedOptions).some((option) => option !== null && option !== undefined)
  );
});

const remainingSplitBudget = computed(() => {
  return activeBudgetStore.getRemainingBudgetAfterReserved(
    productToSave.value.vBudget?.type !== 'customer_budget' ? productToSave.value.vBudget?.id : 0,
    productToSave.value.vBudget?.remaining_budget || 0
  );
});

const splitBudgetBelowMinimum = computed(() => {
  if (showSplits.value && productPrice.value !== undefined) return remainingSplitBudget.value < productPrice.value;

  return false;
});

/* --------------------------------- METHODS -------------------------------- */
function resetValues() {
  //Reset the productToSave values to the initial values
  if (selectedProduct.value) {
    // close all sections first
    expandedSection.value = null;
    // open the first section by default
    const firstSection = availableSections.value[0];
    if (firstSection) {
      if (firstSection === 'productOptions' && productOptions.value.length)
        expandedSection.value = `productOptions-${productOptions.value[0].id}`;
      else expandedSection.value = firstSection + '-0';
    }

    productToSave.value.id = selectedProduct.value?.id || 0;
    productToSave.value.selectedOptions = {};

    //Reset splits
    productToSave.value.vBudget = JSON.parse(JSON.stringify(defaultSelectedVbudget.value));

    productToSave.value.budget = showBudgetFields.value
      ? selectedProduct.value?.price || limitations.value.budget_min
      : undefined;
    productToSave.value.metric_goal = showMetricSlider.value ? selectedProduct.value.metric_goal || 0 : undefined;
    productToSave.value.spending_limit = showSpendingSlider.value
      ? selectedProduct.value.spending_limit || 0
      : undefined;

    if (showDateFields.value) {
      const startdate = startOfTomorrow();
      const enddate = subSeconds(addDays(startdate, defaultDuration.value), 1);
      productToSave.value.startdate = formatDateTime(startdate, PJAS_DATE_FORMAT);
      productToSave.value.enddate = formatDateTime(enddate, PJAS_DATE_FORMAT);
    } else {
      productToSave.value.startdate = undefined as any;
      productToSave.value.enddate = undefined as any;
    }

    // If product options exist, check for default values, or clean the selection
    if (productOptions.value.length) {
      productOptions.value.forEach((pOption) => {
        pOption.field_config.cards.forEach((card) => {
          if (card.is_default) {
            productToSave.value.selectedOptions[pOption.id] = card.value;
          }
        });
      });
    } else productToSave.value.selectedOptions = {};
  }
  // trigger form refresh, since we use v-show instead of v-if for sections
  refreshFormKey.value += 1;
}

function calculateProductLimitations() {
  const limitParams = Object.entries(selectedProduct.value?.creation_limits || {});
  if (limitParams.length === 0) return (limitations.value = { ...defaultLimits });

  limitParams.forEach(([key, value]) => {
    const defaultLimit = key.includes('max') ? Infinity : 0;
    limitations.value[key as keyof IProductLimitations] = value || defaultLimit;
  });
}

function saveProduct() {
  if (!selectedProduct.value) return;
  const { id, startdate, enddate, budget, selectedOptions, spending_limit, metric_goal, vBudget } = productToSave.value;
  //IMPORTANT: here you save the product params based on type
  const container: Partial<IProductToSave> = { id };
  if (showMetricSlider.value) container.metric_goal = metric_goal;
  if (showSpendingSlider.value) container.spending_limit = spending_limit;
  if (showBudgetFields.value) container.budget = budget;
  if (productOptions.value.length) container.selectedOptions = selectedOptions;
  if (showDateFields.value) {
    container.startdate = startdate;
    container.enddate = enddate;
    container.budget = budget;
  }
  if (showSplits.value && vBudget?.id) container.vBudget = vBudget;

  emit('saveProduct', selectedProduct.value!, container);
  openedState.value = false;
}

/* ---------------------------------- WATCH --------------------------------- */
watch(selectedProduct, (newValue, oldValue) => {
  if (!oldValue && newValue) {
    nextTick(async () => {
      calculateProductLimitations();
      resetValues();
      if (activeBudgetId.value) await applySuggestedVBudget(activeBudgetId.value);
    });
  }
});

// update slider progress styling
watch(
  [() => productToSave.value.metric_goal, () => productToSave.value.spending_limit, () => productToSave.value.budget],
  () => {
    nextTick(() => {
      updateSliderProgress();
    });
  }
);

function updateSliderProgress() {
  const sliders = document.querySelectorAll('.form-range');
  sliders.forEach((slider: Element) => {
    const input = slider as HTMLInputElement;
    const min = parseFloat(input.min);
    const max = parseFloat(input.max);
    const val = parseFloat(input.value);
    const progress = ((val - min) / (max - min)) * 100;
    input.style.setProperty('--range-progress', `${progress}%`);
  });
}

function findVBudgetInTree(node: IBudgetTreeNode, vbudgetId: number): IProductVBudget | null {
  if (node.vbudget?.data.id === vbudgetId) {
    return {
      id: node.vbudget.data.id,
      name: node.vbudget.data.name ?? 'Unknown',
      type: node.type,
      currency: node.vbudget.data.allocation_currency ?? defaultCurrency,
      total_budget: node.availability?.available_now ?? 0,
      remaining_budget: node.availability?.available_unallocated ?? 0,
      children: [],
    };
  }
  for (const child of node.children || []) {
    const found = findVBudgetInTree(child as IBudgetTreeNode, vbudgetId);
    if (found) return found;
  }
  return null;
}

async function applySuggestedVBudget(activeBudgetId: number) {
  const companyId = order.value.company_id;
  const productId = selectedProduct.value?.id;
  if (!companyId || !productId) return;
  suggestingVBudget.value = true;
  try {
    const result = await suggestVBudget(activeBudgetId, companyId, productId);
    const suggestedId = result.vbudget_id;
    if (!suggestedId) return;
    const suggested = findVBudgetInTree(vBudgetTree.value, suggestedId);
    if (suggested) {
      productToSave.value.vBudget = suggested;
      defaultSelectedVbudget.value = suggested;
    }
  } catch (error) {
    console.error('Error suggesting vbudget:', error);
  } finally {
    suggestingVBudget.value = false;
  }
}

async function fetchSplits(activeBudgetId: number) {
  try {
    if (!activeBudgetId) return (vBudgetTree.value = defaultVBudgetTree);
    const vBudgets = await fetchBudgetTree(activeBudgetId).then((response) => response.data || response);
    vBudgetTree.value = vBudgets.tree;
  } catch (error) {
    console.error('Error fetching splits:', error);
    useToast().error('Failed to fetch splits');
    vBudgetTree.value = defaultVBudgetTree;
  }
}
</script>
<style scoped>
:deep(.modal-product) {
  max-width: 433px;
}

.modal-section {
  border-bottom: 1px solid var(--tblr-border-color);
}

.modal-section:last-child {
  border-bottom: none;
}

.modal-footer {
  background-color: #f8f8f8;
  justify-content: space-between;
  align-items: center;
}

.option-card {
  height: 100%;
  cursor: pointer;
  position: relative;
}

.option-card .selected-icon {
  position: absolute;
  right: 5px;
  top: 5px;
}

.avatar {
  flex: 0 0 70px;
  width: 70px;
  height: 70px;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.avatar img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.product-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
}

.product-subtitle {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  color: #404040 !important;
}

/* the range slider should have a blue background till its grabber thingy... */
:deep(.form-range) {
  appearance: none;
  -webkit-appearance: none;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  position: relative;
}

:deep(.form-range::before) {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: var(--range-progress, 0%);
  background: var(--tblr-primary);
  border-radius: 3px;
  pointer-events: none;
}

:deep(.form-range::-webkit-slider-track) {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  height: 6px;
  border-radius: 3px;
}

:deep(.form-range::-moz-range-track) {
  background: transparent;
  height: 6px;
  border-radius: 3px;
  border: none;
}

:deep(.form-range::-ms-track) {
  background: transparent;
  height: 6px;
  border-radius: 3px;
  border: none;
  color: transparent;
}

:deep(.form-range::-webkit-slider-thumb) {
  appearance: none;
  -webkit-appearance: none;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: var(--tblr-primary);
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
  margin-top: -8px;
}

:deep(.form-range::-moz-range-thumb) {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: var(--tblr-primary);
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  appearance: none;
  -moz-appearance: none;
  position: relative;
  z-index: 2;
  margin-top: -8px;
}

:deep(.form-range::-ms-thumb) {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: var(--tblr-primary);
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
  margin-top: -8px;
}
</style>
